import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Loader2, Sun, Contrast, Droplets, RotateCcw, Image as ImageIcon, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkImageEditor = () => {
  const [artworks, setArtworks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
  });
  const navigate = useNavigate();

  const fetchArtworks = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const res = await axios.get(`${API}/artworks`, { headers: { Authorization: `Bearer ${token}` } });
      setArtworks(res.data.filter(a => a.image_url || a.cloud_display_path));
    } catch {
      toast.error('Failed to load artworks');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchArtworks(); }, [fetchArtworks]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === artworks.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(artworks.map(a => a.id)));
    }
  };

  const resetAdjustments = () => {
    setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
  };

  const hasChanges = adjustments.brightness !== 100 || adjustments.contrast !== 100 || adjustments.saturation !== 100;

  const applyBulkAdjustments = async () => {
    if (selected.size === 0) { toast.error('Select at least one artwork'); return; }
    if (!hasChanges) { toast.error('Adjust at least one setting'); return; }

    const token = localStorage.getItem('admin_token');
    setProcessing(true);
    setProgress({ current: 0, total: selected.size });

    const ids = Array.from(selected);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < ids.length; i++) {
      setProgress({ current: i + 1, total: ids.length });
      try {
        await axios.post(`${API}/artworks/${ids[i]}/bulk-adjust`, adjustments, {
          headers: { Authorization: `Bearer ${token}` },
        });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to adjust ${ids[i]}:`, err);
      }
    }

    setProcessing(false);
    if (successCount > 0) {
      toast.success(`Applied adjustments to ${successCount} artwork${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed on ${failCount} artwork${failCount > 1 ? 's' : ''}`);
    }

    resetAdjustments();
    setSelected(new Set());
    fetchArtworks();
  };

  const previewStyle = {
    filter: `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`,
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="bulk-editor-loading">
      <Loader2 className="animate-spin text-accent" size={32} />
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-6 pb-16 px-4 sm:px-6 lg:px-8" data-testid="bulk-image-editor">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl font-light" data-testid="editor-title">Bulk Image Editor</h1>
              <p className="font-sans text-xs text-muted-foreground mt-0.5">
                {selected.size} of {artworks.length} selected
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
            {selected.size === artworks.length ? <CheckSquare size={14} className="mr-1.5" /> : <Square size={14} className="mr-1.5" />}
            {selected.size === artworks.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6">
          {/* Artwork Grid */}
          <div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="artwork-grid">
              {artworks.map(art => (
                <button
                  key={art.id}
                  onClick={() => toggleSelect(art.id)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    selected.has(art.id) ? 'border-accent ring-2 ring-accent/30' : 'border-transparent hover:border-border'
                  }`}
                  data-testid={`artwork-tile-${art.id}`}
                >
                  <img
                    src={art.thumb_url || art.image_url}
                    alt={art.title}
                    className="w-full h-full object-cover"
                    style={selected.has(art.id) && hasChanges ? previewStyle : {}}
                  />
                  {selected.has(art.id) && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center">
                      <Check size={12} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-[9px] text-white truncate font-sans">{art.title}</p>
                  </div>
                </button>
              ))}
            </div>
            {artworks.length === 0 && (
              <div className="text-center py-16">
                <ImageIcon size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No artworks found</p>
              </div>
            )}
          </div>

          {/* Adjustment Panel */}
          <div className="bg-card rounded-lg border border-border p-5 h-fit sticky top-24" data-testid="adjustment-panel">
            <h2 className="font-sans text-sm font-medium mb-5">Adjustments</h2>

            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-sans text-xs text-muted-foreground flex items-center gap-1.5">
                    <Sun size={12} /> Brightness
                  </label>
                  <span className="font-sans text-xs font-medium" data-testid="brightness-value">{adjustments.brightness}%</span>
                </div>
                <Slider
                  value={[adjustments.brightness]}
                  onValueChange={([v]) => setAdjustments(p => ({ ...p, brightness: v }))}
                  min={50} max={150} step={1}
                  data-testid="brightness-slider"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-sans text-xs text-muted-foreground flex items-center gap-1.5">
                    <Contrast size={12} /> Contrast
                  </label>
                  <span className="font-sans text-xs font-medium" data-testid="contrast-value">{adjustments.contrast}%</span>
                </div>
                <Slider
                  value={[adjustments.contrast]}
                  onValueChange={([v]) => setAdjustments(p => ({ ...p, contrast: v }))}
                  min={50} max={150} step={1}
                  data-testid="contrast-slider"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-sans text-xs text-muted-foreground flex items-center gap-1.5">
                    <Droplets size={12} /> Saturation
                  </label>
                  <span className="font-sans text-xs font-medium" data-testid="saturation-value">{adjustments.saturation}%</span>
                </div>
                <Slider
                  value={[adjustments.saturation]}
                  onValueChange={([v]) => setAdjustments(p => ({ ...p, saturation: v }))}
                  min={0} max={200} step={1}
                  data-testid="saturation-slider"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={resetAdjustments}
                disabled={!hasChanges}
                className="flex-1"
                data-testid="reset-btn"
              >
                <RotateCcw size={12} className="mr-1.5" /> Reset
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={applyBulkAdjustments}
                disabled={processing || selected.size === 0 || !hasChanges}
                className="flex-1 bg-accent hover:bg-accent/90"
                data-testid="apply-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={12} className="mr-1.5 animate-spin" />
                    {progress.current}/{progress.total}
                  </>
                ) : (
                  <>Apply to {selected.size}</>
                )}
              </Button>
            </div>

            {hasChanges && (
              <p className="font-sans text-[10px] text-muted-foreground mt-3 text-center">
                Preview is shown on selected artworks. Changes are permanent.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImageEditor;
